import React, {useContext, useEffect, useRef, useState} from "react";
import {KoerberStage} from './Stage.style';
import Button from '../Button/Button';
import {colors, devices, sizes} from "../../utils/styled/_variables.styled";
import Link from '../Link/Link';
import Icon from '../Icon/Icon';
import {ErrorBoundary} from "react-error-boundary";
import {urlHasParam} from "../../utils/js/params";
import axios from "axios";
import useVisibility from "../../utils/useVisibility";
import Video from "../../utils/Video";
import {StateContext} from "../../context/stateContext";
import objectHash from "object-hash";
import { getWidthAndHeightProps } from "../../utils/js/widthAndHeight";

const Stage = props => {
    const {
        buttonsList, pictureCrops, path, componentType, title, titleColor, leadText, leadTextColor,
        callToAction, video, videoUrl, teaserPath, pageLanguage,
        infoTypeTime, contentType, overviewPagePath, overviewPageTarget, readingTime, showArrow, hideTeaserTile, alt
    } = props;

    const [scrollDistance, setScrollDistance] = useState(0);
    const [arrowPosition, setArrowPosition] = useState(0);
    const [viewHeight, setViewHeight] = useState(window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight);
    const [toScroll, setToScroll] = useState(null);
    const sectionRef = useRef(null);
    const contentRef = useRef(null);
    const [teaser, setTeaser] = useState({});

    const [stageRef, isStageVisible] = useVisibility();
    const { breadcrumb } = useContext(StateContext);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, {passive: true});
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        setToScroll(sectionRef.current.getBoundingClientRect().bottom);
        handleResize();

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    useEffect(() => {
		const teaserPath = props.teaserPath;
        if (teaserPath) {
            if (teaserPath.startsWith('/content/dam')) {
                axios.get((teaserPath + ".json").replace('/content/dam', '/api/assets'))
                    .then(({data: {properties: {elements}}}) => {
                        const getValue = ({variations, value}) => variations[pageLanguage] ? variations[pageLanguage].value : value;

                        setTeaser(
                            {
                                title: getValue(elements.title),
                                text: getValue(elements.text),
                                callToAction: getValue(elements.callToAction),
                                callToActionLink: getValue(elements.callToActionLink),
                                openOnANewBrowserTab: getValue(elements.openOnANewBrowserTab)
                            }
                        );
                    })
                    .catch(error => {
                        console.error(error);
                    });
            } else {
                let teaserEndPoint = `${encodeURIComponent(`getTeasers;currentDate=${new Date().toISOString()};variation=${pageLanguage}`)}`;
                axios.get(`/graphql/execute.json/koerber/${teaserEndPoint}`)
                    .then(({data: {data: {teaserList: {items}}}}) => {
                        setTeaser(items.length > 0 ? items[0] : {});
                    }).catch(error => console.error(error));
            }
        } else {
            let teaserEndPoint = `${encodeURIComponent(`getTeasers;currentDate=${new Date().toISOString()};variation=${pageLanguage}`)}`;
            axios.get(`/graphql/execute.json/koerber/${teaserEndPoint}`)
                .then(({data: {data: {teaserList: {items}}}}) => {
                    setTeaser(items.length > 0 ? items[0] : {});
                }).catch(error => console.error(error));
        }

    }, [props.cqPath, props.teaserPath]);

    const handleResize = () => {
        setViewHeight(window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight);

        const childrem = contentRef.current;
        const distance = (childrem.offsetTop + childrem.offsetHeight);

        setArrowPosition(window.innerHeight - distance);
    }

    const handleScroll = () => {
        const component = sectionRef.current;
        if (component) {
            const componentPosition = component.getBoundingClientRect();
            const scrollHeight = document.documentElement.scrollHeight;
            setViewHeight(window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight);
            const scrollVertical = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
            setToScroll(componentPosition.bottom);

            requestAnimationFrame(() => {
                if (scrollVertical + viewHeight >= scrollHeight) {
                    setScrollDistance(1230); // If scroll reachs the end of page but still in the viewport
                } else {
                    setScrollDistance(Math.abs(componentPosition.top));
                }
            });
        }
    };

    const scrollToNextComponent = () => {
        window.scroll({top: window.scrollY + toScroll - 88, behavior: 'smooth'});
    };

    useEffect(() => {
        if(sectionRef.current){
            sectionRef.current.parentElement.classList.add(`unset__grid`);
            sectionRef.current.style.opacity = "100%";
        }

    }, []);

    const cropImages = isStageVisible && (pictureCrops && Object.keys(pictureCrops).length || path)

    return (
        <>
            <KoerberStage style={{opacity: "0%", position: "relative"}} picture={isStageVisible && pictureCrops} path={path} ref={sectionRef} hideTeaserTile={hideTeaserTile}
                          scrollDistance={scrollDistance} viewHeight={viewHeight} teaserTitle={teaser.teaserTitle}
                          arrowPosition={arrowPosition} titleColor={titleColor} leadTextColor={leadTextColor}>
            {cropImages && componentType === 'large' ?
            <picture>
                <source media={devices.mobile} srcSet={pictureCrops.stageL1Mobile}/>
                <source media={devices.tabletLarge} srcSet={pictureCrops.stageL1Tablet}/>
                <source media={devices.desktop} srcSet={pictureCrops.stageL1Desktop}/>
                <img {...getWidthAndHeightProps(sectionRef)} className="stage__bg" 
                    src={cropImages? pictureCrops.stageL1Desktop: path} 
                    alt={alt?? ''} />
            </picture>
            : cropImages && componentType === 'small'?
            <picture>
                <source media={devices.mobile} srcSet={pictureCrops.stageL2Mobile} />
                <source media={devices.tabletLarge} srcSet={pictureCrops.stageL2Tablet} />
                <source media={devices.desktop} srcSet={pictureCrops.stageL2Desktop}/>
                <img {...getWidthAndHeightProps(sectionRef)} className="stage__bg" 
                    src={cropImages? pictureCrops.stageL2Desktop: path} 
                    alt={alt?? ''} />
            </picture>
            :
            <></>}
                <div ref={stageRef}
                     className={`stage ${componentType === 'large' || componentType === 'small'  ? ' stage__padding' : ''}`}>
                     {(componentType === 'large' || componentType === 'small') && (video || videoUrl) &&
                        <Video className="stage__banner-video" src={video || videoUrl} autoPlay loop muted playsInline/>
                    }
                    <div
                        className={`stage__grid stage${componentType === 'large' ? '__banner' : componentType === 'small' ? '__banner stage__banner-small' : '__content'} ${componentType === 'large' ? "stage-l1" : ''}`}>
                        <div
                            className={`stage${componentType === 'large' || componentType === 'small' ? '__banner-container' : '__content-container'}`}
                            ref={contentRef}>
                            {componentType === 'large' && <div className="stage-scroll">
                                <div className="scroll-indicator"></div>
                            </div>}
                            <div
                                className={`stage${componentType === 'large' || componentType === 'small' ? '__banner-texts' : '__content-texts'}`}>
                                {infoTypeTime === "true" && componentType !== 'simple' &&
                                    <div className="large__type-time">
                                        {contentType &&
                                            <a
                                                className="label-small content__type"
                                                href={overviewPagePath}
                                                target={overviewPageTarget}>
                                                {contentType}
                                            </a>
                                        }
                                        {readingTime && <>
                                            <Icon icon="eye-filled" color={colors.gray4}/>
                                            <p className="label-small content__time">{readingTime} min</p>
                                        </>}
                                    </div>}
                                {componentType === "simple" && breadcrumb}
                                <h1 className="heading-1">{title}</h1>
                                <p className="leadText">{leadText}</p>
                            </div>
                            {(componentType === 'large' || componentType === 'small') && buttonsList &&
                                <div className="buttons__wrapper">
                                    {buttonsList.map((item, k) => {
                                        let stageClass = item.stageButtonPath === '#contact-form' ? `stage-contact` : '';
                                        return (
                                            <a key={objectHash(item)} href={item.stageButtonPath}
                                               target={item.stageButtonTarget}>
                                                <Button
                                                    id={item.stageButtonId || stageClass}
                                                    className={item.stageButtonId || stageClass}
                                                    inverted={!["blue", "green", "greenlight", "infioswhite"].includes(item.stageButtonColor)} text={item.stageButtonText} noIcon
                                                    primary={item.stageButtonColor === 'white'}
                                                    secondary={item.stageButtonColor === 'transparent'}
                                                    blue={item.stageButtonColor === 'blue'}
                                                    green={item.stageButtonColor === 'green'}
                                                    white={item.stageButtonColor === 'infioswhite'}
                                                    greenlight={item.stageButtonColor === 'greenlight'}/>
                                            </a>
                                        );
                                    })}
                                </div>
                            }
                            {componentType === 'large' && !showArrow && <div className="arrow-animated">
                                <Icon icon={"arrow-bottom"} color={colors.white} onClick={scrollToNextComponent}/>
                            </div>}
                        </div>
                        {componentType === 'large' && teaser.title && !hideTeaserTile &&
                            <a href={teaser.callToActionLink} target={teaser.callToAction} className="stage__banner-box stage-teaser">
                                <div>
                                    <span className="heading-6">{teaser.title}</span>
                                    <p className="stage__banner-box-p p-regular">{teaser.text}</p>
                                </div>
                                <div className="stage__banner-box-link">
                                    <Link className="stage-teaser" link={teaser.callToActionLink} text={teaser.callToAction} target={teaser.openOnANewBrowserTab ? "_blank" : "_self"} animated/>
                                </div>
                            </a>
                        }
                    </div>
                </div>
            </KoerberStage>
        </>

    )
}

const StageHandleErrors = props => {
    return (
        <ErrorBoundary fallback={props.isInEditor || urlHasParam('wcmmode=disabled') ?
            <div style={{minHeight: '50px', display: 'flex', alignItems: 'center', backgroundColor: '#ffe1db'}}><p>The
                stage component is currently experiencing errors. Please try again later.</p></div> : <div></div>}>
            <Stage {...props}/>
        </ErrorBoundary>
    )
}

export default StageHandleErrors;
  